import { onBeforeUnmount, onMounted } from 'vue';

const MOBILE_QUERY = '(max-width: 768px)';
const COLUMN_CLASS = /el-table_\d+_column_\d+/;

function columnKey(cell: Element): string | null {
  const match = cell.className.match(COLUMN_CLASS);
  return match ? match[0] : null;
}

// True when a cell shows nothing the user can see (spacer columns, empty values).
function isBlankCell(cell: Element): boolean {
  if (cell.querySelector('img, svg, input, button, canvas, video, .el-tag, .el-switch')) {
    return false;
  }
  return !cell.textContent?.trim();
}

// Element Plus cells carry no label, so copy each column header onto its cells as `data-label`.
// The mobile stylesheet (see _cms-theme.scss) turns every `.el-table` into a stack of cards and
// prints that label before each value. Tables opt out with the class `table-no-cards`.
function labelTable(table: Element): void {
  const labels = new Map<string, string>();
  table.querySelectorAll('.el-table__header-wrapper th').forEach((th) => {
    const key = columnKey(th);
    const text = th.textContent?.trim();
    if (key && text) labels.set(key, text);
  });

  table.querySelectorAll('.el-table__body td.el-table__cell').forEach((td) => {
    const key = columnKey(td);
    const label = (key && labels.get(key)) || '';
    if (td.getAttribute('data-label') !== label) td.setAttribute('data-label', label);
    td.classList.toggle('is-blank', isBlankCell(td));
  });
}

export function useMobileTableCards(): void {
  let observer: MutationObserver | null = null;
  let query: MediaQueryList | null = null;
  let frame = 0;

  const run = () => {
    frame = 0;
    if (!query?.matches) return;
    document.querySelectorAll('.el-table:not(.table-no-cards)').forEach(labelTable);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(run);
  };

  onMounted(() => {
    query = window.matchMedia(MOBILE_QUERY);
    query.addEventListener('change', schedule);
    // Attributes are not observed, so setting data-label / classes in run() cannot retrigger it
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    query?.removeEventListener('change', schedule);
    if (frame) cancelAnimationFrame(frame);
  });
}
